import type { LucideIcon } from 'lucide-react'
import {
  Bot, Blocks, Database, FlaskConical, FolderTree, Inbox, KeyRound, Library, Phone, RadioTower, Settings, Workflow,
} from 'lucide-react'
import type { NavEntry } from '@retink/web-ui'

export interface ControlProduct {
  id: string
  path: string
  label: string
  icon: LucideIcon
  iconKey: string
  nav: NavEntry[]
}

export const CONTROL_PRODUCTS: Record<string, ControlProduct> = {
  filesystem: {
    id: 'filesystem',
    path: '/filesystem',
    label: 'Files',
    icon: FolderTree,
    iconKey: 'folder-tree',
    nav: [
      { to: '/filesystem', label: 'Files', icon: FolderTree, end: true },
      { to: '/filesystem/admin', label: 'Admin', icon: Settings },
    ],
  },
  sqldb: {
    id: 'sqldb',
    path: '/sqldb',
    label: 'Database',
    icon: Database,
    iconKey: 'database',
    nav: [{ to: '/sqldb', label: 'Databases', icon: Database, end: true }],
  },
  knowledge: {
    id: 'knowledge',
    path: '/knowledge',
    label: 'Knowledge',
    icon: Library,
    iconKey: 'library',
    nav: [
      { to: '/knowledge', label: 'Knowledge bases', icon: Library, end: true },
      { to: '/knowledge/ai-test', label: 'AI Test', icon: FlaskConical },
    ],
  },
  flows: {
    id: 'flows',
    path: '/flows',
    label: 'Flows',
    icon: Workflow,
    iconKey: 'workflow',
    nav: [
      { to: '/flows', label: 'Flows', icon: Workflow, end: true },
      { to: '/flows/steps', label: 'Steps', icon: Blocks },
      { to: '/flows/flow-auths', label: 'Credentials', icon: KeyRound },
    ],
  },
  agents: {
    id: 'agents',
    path: '/agents',
    label: 'AI Agents',
    icon: Bot,
    iconKey: 'bot',
    nav: [
      { to: '/agents', label: 'Agents', icon: Bot, end: true },
      { to: '/agents/models', label: 'Models', icon: Blocks },
    ],
  },
}

// Platform SERVICES — not launchable apps but always-on platform capabilities
// whose tenant surface is configuration (channels first). They ride the same
// ControlProduct shape so the shell's nav selection and rail marking need no
// second mechanism, and render under the rail's "Services" section.
export const CONTROL_SERVICES: Record<string, ControlProduct> = {
  channels: {
    id: 'channels',
    path: '/channels',
    label: 'Channels',
    icon: RadioTower,
    iconKey: 'radio-tower',
    nav: [
      { heading: 'Channels' },
      { to: '/channels/routes', label: 'Routes', icon: RadioTower },
      { to: '/channels/queues', label: 'Queues', icon: Inbox },
      { to: '/channels/voice', label: 'Voice', icon: Phone },
    ],
  },
}

export function productForPath(pathname: string): ControlProduct | undefined {
  return [...Object.values(CONTROL_PRODUCTS), ...Object.values(CONTROL_SERVICES)].find(
    (product) => pathname === product.path || pathname.startsWith(`${product.path}/`),
  )
}
